import React, { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { JIRA_PROJECT_TYPES } from '@jira/platform__project-types/src/project-types.tsx';
import { associateProjectToFieldSchemeModalEntryPoint } from '@jira/project-settings__associate-project-to-field-scheme-modal/src/associateProjectToFieldSchemeModalEntryPoint.entrypoint.tsx';
import { ModalDropdownItemTrigger } from '@atlassian/entry-points/modal-dropdown-item-trigger';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ActionMenuItemLegacyPageOptInModalQuery } from './__generated__/ActionMenuItemLegacyPageOptInModalQuery.graphql';

const menuItemLabel = defineMessage({
	id: 'admin-pages-field-schemes.ui.legacy-cmp-project.action-menu-item',
	defaultMessage: 'Use a different scheme',
	description:
		'Label for new menu item in actions dropdown on legacy CMP Project Settings Fields page',
});

const defaultRedirectHandler = (url: string) => {
	globalThis.window.location.href = url;
};
const defaultReloadHandler = () => {
	globalThis.window.location.reload();
};

type Props = {
	projectId: string;
	restoreFocusElement?: HTMLElement;
	// These are just for testing
	onRedirect?: (url: string) => void;
	onReload?: () => void;
};
export const ActionMenuItem = (props: Props): React.ReactNode => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const {
		projectId,
		restoreFocusElement,
		onRedirect = defaultRedirectHandler,
		onReload = defaultReloadHandler,
	} = props;

	const projectData = useLazyLoadQuery<ActionMenuItemLegacyPageOptInModalQuery>(
		graphql`
			query ActionMenuItemLegacyPageOptInModalQuery($cloudId: ID!, $projectId: String!) {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectId) {
					key
					projectType
				}
			}
		`,
		{ cloudId, projectId },
	);

	const entrypointParams = useMemo(() => {
		return {
			cloudId,
			projectId,
		};
	}, [cloudId, projectId]);

	const nfaProjectFieldSettingsUrl = useMemo(() => {
		const { key, projectType } = projectData?.jira_projectByIdOrKey ?? {};
		const projectTypeMap: Record<string, string> = {
			// /jira/software/c/projects/CKP/settings/fields
			[JIRA_PROJECT_TYPES.SOFTWARE]: 'software',
			// /jira/core/c/projects/JWMCMPDTAR/settings/fields
			[JIRA_PROJECT_TYPES.BUSINESS]: 'core',
			// /jira/servicedesk/c/projects/JSMCMPFR/settings/fields
			[JIRA_PROJECT_TYPES.SERVICE_DESK]: 'servicedesk',
		};

		if (!key || !projectType || !projectTypeMap[projectType]) {
			return undefined;
		}

		return `/jira/${projectTypeMap[projectType]}/c/projects/${key}/settings/fields`;
	}, [projectData?.jira_projectByIdOrKey]);

	const entrypointProps = useMemo(() => {
		return {
			onClose: () => {
				if (restoreFocusElement) {
					// Give time for modal close animation to complete before focusing element
					// requestAnimationFrame() and requestIdleCallback() didn't seem to work here
					// so we fall back to the good old setTimeout().
					// This workaround will go away once we fully migrate the page to React.
					setTimeout(() => {
						restoreFocusElement.focus();
					}, 400);
				}
			},
			onSaveComplete: (isUsingNfaScheme: boolean) => {
				// Since we're on a legacy page...
				if (isUsingNfaScheme && nfaProjectFieldSettingsUrl) {
					// redirect to the NFA scheme page
					onRedirect(nfaProjectFieldSettingsUrl);
				} else {
					// refresh the whole page to see updated heading and fields
					onReload();
				}
			},
		};
	}, [nfaProjectFieldSettingsUrl, restoreFocusElement, onRedirect, onReload]);

	// For some reason Modal does not seem to work when using it with `component={BasicLink}`
	// Due to this, we've kept it as a button
	return (
		<ModalDropdownItemTrigger
			entryPoint={associateProjectToFieldSchemeModalEntryPoint}
			entryPointParams={entrypointParams}
			entryPointProps={entrypointProps}
		>
			{formatMessage(menuItemLabel)}
		</ModalDropdownItemTrigger>
	);
};
